import { For } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { Library, ScanLine, Sparkles, Store, User } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { pressable, staggerItem } from "@/lib/motion";
import { useI18n } from "@/providers/i18n-provider";
import { appRoute } from "./route";

/**
 * 🏡 Aldea (Cottage / Home) — home provisional de marca FamiDex. Reemplaza el
 * dashboard todo-céntrico del template. Da la bienvenida, resume el flujo del
 * coleccionista y enlaza a las superficies reales del 🎯 (catálogo, escáner,
 * colección). El feed de comunidad lo rellena Wave 1 (agente C) sobre este mismo
 * tab, sin editar el shell.
 */
function CottageHome() {
  const { t } = useI18n();
  const quickActions = [
    {
      to: "/catalog",
      label: t(msg`Explorar catálogo`),
      desc: t(msg`Miles de sets Sylvanian`),
      icon: Library,
    },
    {
      to: "/scanner",
      label: t(msg`Escanear`),
      desc: t(msg`Código de barras → detalle`),
      icon: ScanLine,
    },
    {
      to: "/collection",
      label: t(msg`Mi colección`),
      desc: t(msg`Progreso por serie`),
      icon: Sparkles,
    },
    { to: "/market", label: t(msg`Mercado`), desc: t(msg`Bellotas y recompensas`), icon: Store },
  ] as const;

  return (
    <div class="flex flex-col gap-8" data-testid="cottage-home">
      {/* Hero de bienvenida */}
      <section class="overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-6 shadow-sm backdrop-blur-md">
        <p class="text-xs font-bold uppercase tracking-widest text-primary">
          {t(msg`Bienvenido a tu aldea`)}
        </p>
        <h1 class="mt-1 text-2xl font-semibold leading-tight text-foreground">
          {t(msg`Colecciona, cataloga y comparte tu mundo Sylvanian`)}
        </h1>
        <p class="mt-2 text-sm text-muted-foreground">
          {t(
            msg`Escanea un set, sigue tu progreso por serie y haz crecer tu villa. Todo funciona sin conexión y sin necesidad de cuenta para ojear el catálogo.`,
          )}
        </p>
        <div class="mt-4 flex flex-wrap gap-3">
          <Link
            to="/catalog"
            ref={(el: HTMLAnchorElement) => pressable(el, { hapticMs: 5 })}
            class="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md"
          >
            <Library class="h-4 w-4" />
            {t(msg`Explorar catálogo`)}
          </Link>
          <Link
            to="/scanner"
            ref={(el: HTMLAnchorElement) => pressable(el, { hapticMs: 5 })}
            class="inline-flex items-center gap-2 rounded-full border-2 border-primary px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
          >
            <ScanLine class="h-4 w-4" />
            {t(msg`Escanear un set`)}
          </Link>
        </div>
      </section>

      {/* Accesos rápidos */}
      <section>
        <h2 class="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {t(msg`Accesos rápidos`)}
        </h2>
        <div class="grid grid-cols-2 gap-3">
          <For each={quickActions}>
            {(action, index) => (
              <Link
                to={action.to}
                ref={(el: HTMLAnchorElement) => {
                  staggerItem(el, index());
                  pressable(el, { scale: 0.97 });
                }}
                class="group flex flex-col gap-2 rounded-xl border border-border/60 bg-card/60 p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
              >
                <span class="grid h-10 w-10 place-items-center rounded-lg bg-accent text-accent-foreground">
                  <action.icon class="h-5 w-5" />
                </span>
                <span class="text-sm font-semibold text-foreground">{action.label}</span>
                <span class="text-xs text-muted-foreground">{action.desc}</span>
              </Link>
            )}
          </For>
        </div>
      </section>

      {/* Placeholder del feed de comunidad — lo cablea Wave 1 (agente C). */}
      <section
        class="rounded-xl border border-dashed border-border/70 bg-card/40 p-6 text-center"
        data-testid="community-feed-placeholder"
      >
        <User class="mx-auto h-8 w-8 text-muted-foreground/60" />
        <p class="mt-2 text-sm font-medium text-foreground">{t(msg`Tu feed de la comunidad`)}</p>
        <p class="mt-1 text-xs text-muted-foreground">
          {t(msg`Pronto verás aquí las publicaciones de coleccionistas a los que sigues.`)}
        </p>
      </section>
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: CottageHome,
});
